/**
 * 챗봇 전역 상태 (Context).
 *
 * <p>App 최상단에서 감싸 모든 페이지가 동일 챗봇 상태 공유.
 *
 * <h3>상태</h3>
 *  - hidden        : 영구 숨김 여부 (localStorage 보존)
 *  - open          : 대화창 열림 (FAB 클릭 토글)
 *  - messages      : 화면에 표시되는 메시지 배열 (시간 오름차순)
 *  - hasMore       : 위로 더 옛 메시지 있는지
 *  - oldestId      : 다음 페이지 cursor
 *  - usage         : { todayUsed, monthUsed, dailyLimit, remainingToday }
 *  - sending       : 응답 streaming 중 (입력/전송 차단)
 *  - streamingText : 현재 받고 있는 ASSISTANT 응답 텍스트 (실시간)
 *
 * <h3>액션</h3>
 *  - toggleOpen    : 대화창 열기/닫기 (FAB 클릭)
 *  - hide          : FAB 까지 영구 숨김
 *  - show          : 다시 표시
 *  - sendMessage   : 메시지 전송 + streaming 처리
 *  - loadMore      : 위로 스크롤 시 옛 메시지 로드
 *  - resetSession  : 세션 리셋
 *  - refreshUsage  : 사용량 재조회 (메시지 완료 후 자동 호출)
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { chatbotApi } from "./api/chatbotApi";

const HIDDEN_KEY = "chatbot_hidden";
/** 창 크기 축소 모드 — 기기별 취향이라 사용자별로 기억해 둔다(브라우저 로컬). */
const COMPACT_KEY = "chatbot_compact";
const PAGE_SIZE = 30;

const ChatbotContext = createContext(null);

export function useChatbot() {
    const ctx = useContext(ChatbotContext);
    if (!ctx) throw new Error("useChatbot must be used within ChatbotProvider");
    return ctx;
}

/**
 * @param {object} props
 * @param {boolean} props.authenticated  로그인 여부 — false 면 챗봇 컴포넌트 렌더링 안 함
 * @param {React.ReactNode} props.children
 */
export default function ChatbotProvider({ authenticated, children }) {
    // ─── UI 상태 ────────────────────────────────────────────
    const [hidden, setHidden] = useState(() => localStorage.getItem(HIDDEN_KEY) === "true");
    const [open, setOpen] = useState(false);
    const [compact, setCompact] = useState(() => localStorage.getItem(COMPACT_KEY) === "true");

    // ─── 데이터 상태 ────────────────────────────────────────
    const [messages, setMessages] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [oldestId, setOldestId] = useState(null);
    const [usage, setUsage] = useState({
        todayUsed: 0, monthUsed: 0, dailyLimit: 50000, remainingToday: 50000,
    });
    const [sending, setSending] = useState(false);
    const [streamingText, setStreamingText] = useState("");
    const [initialized, setInitialized] = useState(false);

    const abortRef = useRef(null);
    // sendMessage 의 동기 가드 — useState(sending) 은 비동기라 연속 Enter 시 stale 클로저 위험.
    // ref 는 다음 줄에서 즉시 true 가 되므로 같은 tick 내 재호출도 차단된다.
    const sendingRef = useRef(false);

    // ─── streaming 중단 헬퍼 — 창 닫기/숨기기 시 호출 ────────
    // 백엔드는 자체적으로 OpenAI 응답을 끝까지 받아 DB 저장하지만,
    // FE 상태(sending/streamingText) 가 깨끗하게 리셋되도록 abort 한다.
    const abortStreaming = useCallback(() => {
        if (abortRef.current) {
            try { abortRef.current.abort(); } catch (_) {}
            abortRef.current = null;
        }
        if (sendingRef.current) {
            sendingRef.current = false;
            setSending(false);
            setStreamingText("");
        }
    }, []);

    // ─── 영구 숨김 토글 ─────────────────────────────────────
    const hide = useCallback(() => {
        abortStreaming();
        localStorage.setItem(HIDDEN_KEY, "true");
        setHidden(true);
        setOpen(false);
    }, [abortStreaming]);

    const show = useCallback(() => {
        localStorage.removeItem(HIDDEN_KEY);
        setHidden(false);
    }, []);

    // ─── 대화창 열기/닫기 ───────────────────────────────────
    const toggleOpen = useCallback(() => {
        setOpen((v) => !v);
    }, []);

    // ─── 창 크기 축소 토글 ──────────────────────────────────
    // 챗봇은 화면 우하단에 떠 있어 좁은 노트북에서는 본문(표·버튼)을 가린다.
    // 기본을 이미 작게 잡았고, 이 토글로 한 단계 더 줄인다. 선택은 로컬에 기억.
    const toggleCompact = useCallback(() => {
        setCompact((v) => {
            const next = !v;
            if (next) localStorage.setItem(COMPACT_KEY, "true");
            else localStorage.removeItem(COMPACT_KEY);
            return next;
        });
    }, []);

    // ─── 초기 로드 (열림 + 인증 + 미초기화 시) ────────────────
    useEffect(() => {
        if (!authenticated || !open || initialized) return;
        let cancelled = false;
        (async () => {
            try {
                const [hist, use] = await Promise.all([
                    chatbotApi.getHistory({ limit: PAGE_SIZE }),
                    chatbotApi.getUsage(),
                ]);
                if (cancelled) return;
                setMessages(hist?.messages || []);
                setHasMore(!!hist?.hasMore);
                setOldestId(hist?.oldestId ?? null);
                setUsage(use || usage);
                setInitialized(true);
            } catch (e) {
                console.error("[Chatbot] 초기 로드 실패", e);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticated, open, initialized]);

    // ─── 사용량 재조회 ──────────────────────────────────────
    const refreshUsage = useCallback(async () => {
        try {
            const u = await chatbotApi.getUsage();
            setUsage(u);
        } catch (_) {}
    }, []);

    // ─── 옛 메시지 로드 (무한 스크롤) ─────────────────────────
    const loadMore = useCallback(async () => {
        if (!hasMore || oldestId == null) return;
        try {
            const hist = await chatbotApi.getHistory({ beforeId: oldestId, limit: PAGE_SIZE });
            setMessages((prev) => [...(hist?.messages || []), ...prev]);
            setHasMore(!!hist?.hasMore);
            if (hist?.oldestId != null) setOldestId(hist.oldestId);
        } catch (e) {
            console.error("[Chatbot] 옛 메시지 로드 실패", e);
        }
    }, [hasMore, oldestId]);

    // ─── 메시지 전송 (streaming) ────────────────────────────
    const sendMessage = useCallback((content) => {
        const text = (content || "").trim();
        // ref 우선 가드 — 같은 tick 내 두 번째 호출도 즉시 차단.
        if (!text || sendingRef.current) return;
        sendingRef.current = true;

        // 즉시 USER 메시지를 화면에 추가 (낙관적 UI)
        const tempUserMsg = {
            id: `temp-user-${Date.now()}`,
            role: "USER",
            content: text,
            createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, tempUserMsg]);
        setStreamingText("");
        setSending(true);

        let accumulated = "";
        const ctrl = chatbotApi.streamMessage(text, {
            onToken: (chunk) => {
                accumulated += chunk;
                setStreamingText(accumulated);
            },
            onDone: (done) => {
                // streamingText 를 정식 메시지로 commit
                const assistantMsg = {
                    id: done?.messageId ?? `temp-ai-${Date.now()}`,
                    role: "ASSISTANT",
                    content: accumulated,
                    totalTokens: done?.totalTokens,
                    createdAt: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, assistantMsg]);
                setStreamingText("");
                setSending(false);
                sendingRef.current = false;
                refreshUsage();
            },
            onError: (err) => {
                console.warn("[Chatbot] streaming 에러", err);
                // 에러 메시지를 ASSISTANT 자리로 표시 (재시도는 사용자가)
                const errMsg = {
                    id: `temp-err-${Date.now()}`,
                    role: "ASSISTANT",
                    content: `⚠️ ${err?.message || "오류가 발생했습니다."}`,
                    createdAt: new Date().toISOString(),
                    isError: true,
                };
                setMessages((prev) => [...prev, errMsg]);
                setStreamingText("");
                setSending(false);
                sendingRef.current = false;
                refreshUsage();
            },
        });
        abortRef.current = ctrl;
        // sending 을 의존성에 넣지 않는다 — ref 로 동기 가드 → 클로저 stale 없음.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshUsage]);

    // ─── 세션 리셋 ──────────────────────────────────────────
    const resetSession = useCallback(async () => {
        try {
            await chatbotApi.reset();
            // 화면에 즉시 SYSTEM_RESET 마커 append
            setMessages((prev) => [...prev, {
                id: `temp-reset-${Date.now()}`,
                role: "SYSTEM_RESET",
                content: null,
                createdAt: new Date().toISOString(),
            }]);
        } catch (e) {
            console.error("[Chatbot] 리셋 실패", e);
        }
    }, []);

    // ─── unmount 시 streaming abort ─────────────────────────
    useEffect(() => () => {
        if (abortRef.current) abortRef.current.abort();
    }, []);

    const value = useMemo(() => ({
        // UI
        hidden, open, compact,
        // 데이터
        messages, hasMore, oldestId, usage, sending, streamingText,
        // 액션
        toggleOpen, toggleCompact, hide, show, sendMessage, loadMore, resetSession, refreshUsage,
        // 인증 노출 여부
        visible: authenticated && !hidden,
    }), [hidden, open, compact, messages, hasMore, oldestId, usage, sending, streamingText,
         toggleOpen, toggleCompact, hide, show, sendMessage, loadMore, resetSession, refreshUsage,
         authenticated]);

    return (
        <ChatbotContext.Provider value={value}>
            {children}
        </ChatbotContext.Provider>
    );
}
